import { Pipe, PipeTransform } from '@angular/core';
import { Frequency } from '../models/frequency.model';
import {
  TrashAndRecyclingFrequency,
  TrashAndRecyclingServiceDetail,
  TrashAndRecyclingServiceOptions,
} from '../models/service-details.model';
import { sortFrequency, sortTrashAndRecyclingFrequency } from '../utils/sort-frequency';

@Pipe({
  name: 'adaptTrashAndRecyclingServiceOptions',
  standalone: true,
})
export class AdaptTrashAndRecyclingServiceOptionsPipe implements PipeTransform {
  public transform(value: { items: TrashAndRecyclingServiceDetail[] } | null): TrashAndRecyclingServiceOptions | null {
    if (value?.items?.length) {
      const options = value.items;

      return this.buildBaseOptions(options);
    }

    return null;
  }

  private buildBaseOptions(options: TrashAndRecyclingServiceDetail[]): TrashAndRecyclingServiceOptions {
    const optionsMatrix = new Map<string, TrashAndRecyclingServiceDetail>();
    options.forEach((item) => {
      // key in a format: <trashSize>-<trashFrequency>-<trashExtraCount>x<recyclingSize>-<recyclingFrequency>-<recyclingExtraCount>
      const key =
        item.trashWasteContainerSize +
        '-' +
        item.trashFrequency +
        '-' +
        item.extraTrashContainersCount +
        'x' +
        item.recyclingWasteContainerSize +
        '-' +
        item.recyclingFrequency +
        '-' +
        item.extraRecyclingContainersCount;
      optionsMatrix.set(key, item);
    });
    const sizeOptions: Set<{ size: number; desc: string; descRecycling: string }> = new Set();
    let isConfigurationEqual = true;
    const trashSizeOptions: Set<{ size: number; desc: string }> = new Set();
    const recyclingSizeOptions: Set<{ size: number; desc: string }> = new Set();

    options.forEach((item) => {
      sizeOptions.add({
        size: item.trashWasteContainerSize,
        desc: item.trashWasteContainerDescription,
        descRecycling: item.recyclingWasteContainerDescription,
      });
      trashSizeOptions.add({
        size: item.trashWasteContainerSize,
        desc: item.trashWasteContainerDescription,
      });
      recyclingSizeOptions.add({
        size: item.recyclingWasteContainerSize,
        desc: item.recyclingWasteContainerDescription,
      });
      isConfigurationEqual = isConfigurationEqual && item.trashWasteContainerSize === item.recyclingWasteContainerSize;
    });

    const sizes = [...sizeOptions].reduce((previousSizes, el) => {
      const filteredOptions = options.filter((item) => item.trashWasteContainerSize === el.size);
      const frequencyOptions: Set<TrashAndRecyclingFrequency> = new Set();
      filteredOptions.forEach(({ trashFrequency, recyclingFrequency }) => {
        const isAlreadyAdded = [...frequencyOptions].some(
          (elem) => elem.trashFrequency === trashFrequency && elem.recyclingFrequency === recyclingFrequency,
        );
        if (isAlreadyAdded) return;
        frequencyOptions.add({ trashFrequency, recyclingFrequency });
      });
      return { ...previousSizes, [el.size]: sortTrashAndRecyclingFrequency([...frequencyOptions]) };
    }, {});

    const trash = [...trashSizeOptions].reduce((previousSizes, option) => {
      const filteredOptions = options.filter((item) => item.trashWasteContainerSize === option.size);
      const frequencyOptions: Set<Frequency> = new Set();
      filteredOptions.forEach(({ trashFrequency }) => {
        if (frequencyOptions.has(trashFrequency)) return;
        frequencyOptions.add(trashFrequency);
      });
      return {
        ...previousSizes,
        [option.size]: {
          frequencies: sortFrequency([...frequencyOptions]),
          description: option.desc,
        },
      };
    }, {});

    const recycling = [...recyclingSizeOptions].reduce((previousSizes, option) => {
      const filteredOptions = options.filter((item) => item.recyclingWasteContainerSize === option.size);
      const frequencyOptions: Set<Frequency> = new Set();
      filteredOptions.forEach(({ recyclingFrequency }) => {
        if (frequencyOptions.has(recyclingFrequency)) return;
        frequencyOptions.add(recyclingFrequency);
      });
      return {
        ...previousSizes,
        [option.size]: {
          frequencies: sortFrequency([...frequencyOptions]),
          description: option.desc,
        },
      };
    }, {});

    return {
      items: options,
      sizes,
      trash,
      recycling,
      quantityLimits: { min: 1, max: 2 },
      recyclingQuantityLimits: { min: 1, max: 2 },
      seasonDurationOptions: [3, 4, 5, 6, 7, 8, 9],
      isConfigurationEqual,
      optionsMatrix,
    };
  }
}
